import { useLatestRef, useOnChangeEffect } from "@mirage-web/utils/react-helpers";
import { QueryKey } from "@tanstack/react-query";
import { z } from "zod";

import { ApiHost } from "../api-client";
import { NetworkingLayerConfig } from "../config";

import { createMutationBaseMixin, MutationBaseMixin } from "./mixins/base-mutation";
import { createQueryBaseMixin, QueryBaseMixin } from "./mixins/base-query";
import {
  openSSEStreamConnection,
  type OpenSSEStreamConnectionOptions,
  type OpenSSEStreamConnectionResult,
  type SSEEventSchemasMap,
  type SSEReconnectOptions,
} from "./open-sse-stream-connection";
import {
  createStreamedQueryDefinition,
  type StreamedQueryDefinition,
  type StreamedQueryDefinitionOptions,
} from "./streamed-query";
import {
  type CallerCombinedVariables,
  type MutationBodySchema,
  MutationDefinitionOptions,
  QueryDefinitionOptions,
} from "./types";

interface SSEStreamDefinitionOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> {
  host: ApiHost;
  path: Path;
  paramsSchema?: ParamsSchema;
  headers?: Record<string, string>;
  reconnect?: SSEReconnectOptions;
  events: EventSchemas;
}

type SSEStreamConnectionOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> = CallerCombinedVariables<Path, ParamsSchema> & OpenSSEStreamConnectionOptions<EventSchemas>;

type SSEStreamHookOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> = CallerCombinedVariables<Path, ParamsSchema> &
  Pick<
    OpenSSEStreamConnectionOptions<EventSchemas>,
    "headers" | "reconnect" | "sessionStorageKey" | "onEvent" | "onError"
  >;

interface SSEStreamDefinition<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> {
  openStreamConnection: (
    options: SSEStreamConnectionOptions<Path, ParamsSchema, EventSchemas>
  ) => OpenSSEStreamConnectionResult;
  /** Keeps a connection open while mounted, reopening it when its params change. */
  useOnStreamEvents: (options: SSEStreamHookOptions<Path, ParamsSchema, EventSchemas>) => void;
}

export interface NetworkingLayerDefinitions<DelegatedErrorParams> {
  createQueryRequestDefinition: <
    TQueryKey extends QueryKey,
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: QueryDefinitionOptions<
      TQueryKey,
      Path,
      ParamsSchema,
      ResponseSchema,
      TransformedResult
    >
  ) => QueryBaseMixin<TQueryKey, Path, ParamsSchema, ResponseSchema, TransformedResult>;

  createMutationRequestDefinition: <
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    BodySchema extends MutationBodySchema = z.ZodVoid,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: MutationDefinitionOptions<
      Path,
      ParamsSchema,
      ResponseSchema,
      BodySchema,
      TransformedResult
    >
  ) => MutationBaseMixin<Path, ParamsSchema, BodySchema, TransformedResult, DelegatedErrorParams>;

  createSSEStreamDefinition: <
    Path extends string,
    ParamsSchema extends z.ZodTypeAny = z.ZodVoid,
    EventSchemas extends SSEEventSchemasMap = SSEEventSchemasMap,
  >(
    definitionOptions: SSEStreamDefinitionOptions<Path, ParamsSchema, EventSchemas>
  ) => SSEStreamDefinition<Path, ParamsSchema, EventSchemas>;

  createStreamedQueryDefinition: <Variables, Data, Event>(
    definitionOptions: StreamedQueryDefinitionOptions<Variables, Data, Event>
  ) => StreamedQueryDefinition<Variables, Data, Event>;
}

export function createQueryRequestDefinitions<DelegatedErrorParams = unknown>(
  config: NetworkingLayerConfig
): NetworkingLayerDefinitions<DelegatedErrorParams> {
  function createQueryRequestDefinition<
    TQueryKey extends QueryKey,
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: QueryDefinitionOptions<
      TQueryKey,
      Path,
      ParamsSchema,
      ResponseSchema,
      TransformedResult
    >
  ): QueryBaseMixin<TQueryKey, Path, ParamsSchema, ResponseSchema, TransformedResult> {
    return createQueryBaseMixin(config, definitionOptions);
  }

  function createMutationRequestDefinition<
    Path extends string,
    ParamsSchema extends z.ZodTypeAny,
    ResponseSchema extends z.ZodTypeAny,
    BodySchema extends MutationBodySchema = z.ZodVoid,
    TransformedResult extends object = z.infer<ResponseSchema>,
  >(
    definitionOptions: MutationDefinitionOptions<
      Path,
      ParamsSchema,
      ResponseSchema,
      BodySchema,
      TransformedResult
    >
  ): MutationBaseMixin<Path, ParamsSchema, BodySchema, TransformedResult, DelegatedErrorParams> {
    return createMutationBaseMixin<
      DelegatedErrorParams,
      Path,
      ParamsSchema,
      ResponseSchema,
      BodySchema,
      TransformedResult
    >(config, definitionOptions);
  }

  function createSSEStreamDefinition<
    Path extends string,
    ParamsSchema extends z.ZodTypeAny = z.ZodVoid,
    EventSchemas extends SSEEventSchemasMap = SSEEventSchemasMap,
  >(
    definitionOptions: SSEStreamDefinitionOptions<Path, ParamsSchema, EventSchemas>
  ): SSEStreamDefinition<Path, ParamsSchema, EventSchemas> {
    function openStreamConnection(
      options: SSEStreamConnectionOptions<Path, ParamsSchema, EventSchemas>
    ) {
      const pathParams = "pathParams" in options ? options.pathParams : undefined;
      const params = "params" in options ? options.params : undefined;
      const parsedParams = definitionOptions.paramsSchema
        ? definitionOptions.paramsSchema.parse(params)
        : (params as Record<string, unknown>);

      return openSSEStreamConnection(
        config,
        {
          host: definitionOptions.host,
          path: definitionOptions.path,
          pathParams,
          params: parsedParams,
          headers: definitionOptions.headers,
          reconnect: definitionOptions.reconnect,
          events: definitionOptions.events,
        },
        options
      );
    }

    function useOnStreamEventsHook(
      options: SSEStreamHookOptions<Path, ParamsSchema, EventSchemas>
    ) {
      const optionsRef = useLatestRef(options);
      const pathParams = "pathParams" in options ? options.pathParams : undefined;
      const params = "params" in options ? options.params : undefined;

      useOnChangeEffect(JSON.stringify([pathParams, params, options.sessionStorageKey]), () => {
        const { disconnect } = openStreamConnection({
          ...options,
          onEvent: (event) => optionsRef.current.onEvent(event),
          onError: (error) => optionsRef.current.onError?.(error),
        });
        return disconnect;
      });
    }

    return {
      openStreamConnection,
      useOnStreamEvents: useOnStreamEventsHook,
    };
  }

  return {
    createQueryRequestDefinition,
    createMutationRequestDefinition,
    createSSEStreamDefinition,
    createStreamedQueryDefinition: <Variables, Data, Event>(
      definitionOptions: StreamedQueryDefinitionOptions<Variables, Data, Event>
    ) => createStreamedQueryDefinition(config.queryClient, definitionOptions),
  };
}
